import React from 'react';
import { Chip, Tooltip, Badge, CircularProgress } from '@mui/material';
import {
  CloudOff,
  CloudQueue,
  Sync,
  SyncProblem,
  Check,
  WifiOff,
  Wifi,
} from '@mui/icons-material';
import { useOfflineStore } from '../../utils/offline/offline-state-manager';

export const OfflineIndicator: React.FC = () => {
  const {
    isOnline,
    connectionQuality,
    syncInProgress,
    syncProgress,
    pendingSyncCount,
    conflictCount,
    startSync,
    pauseSync,
  } = useOfflineStore();


  const getStatusIcon = () => {
    if (!isOnline) {
      return <CloudOff />;
    }

    if (syncInProgress) {
      return <Sync className="rotating" />;
    }

    if (conflictCount > 0) {
      return <SyncProblem />;
    }

    if (pendingSyncCount > 0) {
      return <CloudQueue />;
    }

    return <Check />;
  };

  const getStatusColor = () => {
    if (!isOnline) return 'error';
    if (conflictCount > 0) return 'warning';
    if (pendingSyncCount > 0) return 'info';
    return 'success';
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (syncInProgress) return 'Syncing...';
    if (conflictCount > 0) return `${conflictCount} Conflicts`;
    if (pendingSyncCount > 0) return `${pendingSyncCount} Pending`;
    return 'All Synced';
  };

  const getConnectionIcon = () => {
    switch (connectionQuality) {
      case 'good':
        return <Wifi color="success" />;
      case 'poor':
        return <Wifi color="warning" />;
      case 'offline':
        return <WifiOff color="error" />;
    }
  };

  return (
    <>
      <Tooltip
        title={
          <div>
            <div style={{ marginBottom: 8 }}>
              <strong>Connection Status</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {getConnectionIcon()}
              <span>{connectionQuality === 'offline' ? 'Offline' : `Online (${connectionQuality})`}</span>
            </div>
            {pendingSyncCount > 0 && (
              <div style={{ marginTop: 8 }}>
                {pendingSyncCount} changes pending sync
              </div>
            )}
            {conflictCount > 0 && (
              <div style={{ marginTop: 8 }}>
                {conflictCount} conflicts need resolution
              </div>
            )}
          </div>
        }
      >
        <Badge
          badgeContent={pendingSyncCount + conflictCount}
          color={conflictCount > 0 ? 'warning' : 'info'}
          invisible={pendingSyncCount + conflictCount === 0}
        >
          <Chip
            icon={getStatusIcon()}
            label={getStatusText()}
            color={getStatusColor()}
            size="small"
            onClick={() => {
              if (isOnline && !syncInProgress && pendingSyncCount > 0) {
                startSync();
              } else if (syncInProgress) {
                pauseSync();
              }
            }}
            sx={{
              cursor: 'pointer',
              '& .rotating': {
                animation: 'spin 2s linear infinite',
              },
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        </Badge>
      </Tooltip>

      {syncInProgress && syncProgress && (
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 8 }}>
          <CircularProgress
            variant="determinate"
            value={(syncProgress.current / syncProgress.total) * 100}
            size={20}
            thickness={4}
          />
          <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>
            {syncProgress.current}/{syncProgress.total}
          </span>
        </div>
      )}
    </>
  );
};
